/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { CellType } from '@grapecity/wijmo.grid';
import { InfoBG, InfoText } from 'ui/theme/Color';
import CustomDialog from 'components/modals/common/CustomDialog';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { CommonUtil } from 'utils/CommonUtil';
import { findDailyReportEquipmentHeavyEquipment } from 'apis/setup/SetupDailyReport';
import { getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { WorkEquipmentHeavyEquipment } from 'models/setup/SetupDailyReport';
import { Code } from 'models/common/CommonCode';
import { useMessageBar } from 'hooks/useMessageBar';

type Props = {
  open: boolean;
  close: () => void;
  isReadOnly?: boolean;
  rowData?: WorkEquipmentHeavyEquipment[];
  condition: {
    wkId: string;
    tgtEqpId: string;
    elmPrjId?: string;
    excpId?: string;
    eqpId: string; // 타이틀 노출용
    eqpNm: string; // 타이틀 노출용
  };
  onConfirm?: (rowData: WorkEquipmentHeavyEquipment[]) => void;
};

interface HeavyEquipmentPivotRow {
  prjSpCd?: string;
  prjSpCdNm?: string;
  prjSpDivsCd?: string;
  prjSpDivsCdNm?: string;
  spTpCd?: string;
  elmPrjId?: string;
  excpId?: string;
  [hepTpCd: string]: any; // 중장비유형코드(HE01 등)별 개수
}

/**
 * @desc 작업일보 > 중장비 등록 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.09.09   jshwang12      신규 생성
 * 2026.09.17   jshwang12      새로고침 삭제
 * 2026.09.30   jshwang12      중장비 값이 있는 셀 색상 표시
 */
const SetupDailyReportHeavyEquipmentPopup = ({
  open,
  close,
  isReadOnly = true,
  condition,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const gridRef = useRef<any>();
  const [hepTypes, setHepTypes] = useState<Code[]>([]);
  const [rowData, setRowData] = useState<HeavyEquipmentPivotRow[]>([]);

  /**
   * 단계 기준 중장비유형을 pivot 으로 변경
   * @param rows
   * @param types
   */
  const toPivotRows = (
    rows: WorkEquipmentHeavyEquipment[] | undefined,
    types: Code[]
  ): HeavyEquipmentPivotRow[] => {
    const map = new Map<string, HeavyEquipmentPivotRow>();
    (rows || []).forEach((row) => {
      const key = row.prjSpCd || '';
      if (!map.has(key)) {
        map.set(key, {
          prjSpCd: row.prjSpCd,
          prjSpCdNm: row.prjSpCdNm,
          prjSpDivsCd: row.prjSpDivsCd,
          prjSpDivsCdNm: row.prjSpDivsCdNm,
          spTpCd: row.spTpCd,
          elmPrjId: row.elmPrjId,
          excpId: row.excpId,
        });
      }
      const type = types.find((t) => t.cmnCd === row.hepTpCd);
      if (type?.cmnCd) {
        map.get(key)![type.cmnCd] = row.eqipCnt;
      }
    });
    return Array.from(map.values());
  };

  /**
   * 단계 기준 중장비유형을 pivot 으로 변경
   * @param rows
   * @param types
   */
  const toFlatRows = (
    rows: HeavyEquipmentPivotRow[],
    types: Code[]
  ): WorkEquipmentHeavyEquipment[] => {
    const result: WorkEquipmentHeavyEquipment[] = [];
    (rows || []).forEach((row) => {
      types.forEach((type) => {
        if (!type.cmnCd) return;
        result.push({
          wkId: condition?.wkId,
          tgtEqpId: condition?.tgtEqpId,
          elmPrjId: row.elmPrjId ?? condition?.elmPrjId,
          excpId: row.excpId ?? condition?.excpId,
          spTpCd: row.spTpCd,
          prjSpCd: row.prjSpCd,
          prjSpCdNm: row.prjSpCdNm,
          prjSpDivsCd: row.prjSpDivsCd,
          prjSpDivsCdNm: row.prjSpDivsCdNm,
          hepTpCd: type.cmnCd,
          eqipCnt: Number(row[type.cmnCd] || 0),
        });
      });
    });
    return result;
  };

  useEffect(() => {
    (async () => {
      const types = (await getCommonCodeNamesCondition({ cmnGrCd: 'HEP_TP_CD' })) || [];
      setHepTypes(types);
      if ((props?.rowData || []).length > 0) {
        setRowData(toPivotRows(props?.rowData, types));
      } else {
        handleSearch(condition, types);
      }
    })();
  }, []);

  const handleSave = () => {
    if (!isReadOnly) {
      props?.onConfirm?.(toFlatRows(rowData, hepTypes));
    }
    close();
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.중장비 목록', '중장비 목록'),
      t('setup.label.중장비 목록', '중장비 목록')
    );
  };

  const handleSearch = (params, types: Code[] = hepTypes) => {
    if (params?.wkId && params?.tgtEqpId) {
      findDailyReportEquipmentHeavyEquipment(params?.wkId, params?.tgtEqpId).then((result) =>
        setRowData(toPivotRows(result, types))
      );
    }
  };

  const countColumn = (type: Code) => ({
    binding: type.cmnCd,
    header: type.cmnCdNm || type.cmnCd,
    minWidth: 70,
    width: 90,
    align: 'center',
    isRequired: false,
    isReadOnly: isReadOnly,
    cellTemplate: (params) => (params.value || 0).toLocaleString(),
  });

  const layoutDefinition = useMemo(
    () => [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'prjSpDivsCdNm',
        header: String(t('setup.label.구분', '구분')),
        minWidth: 70,
        width: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'prjSpCdNm',
        header: String(t('setup.label.단계', '단계')),
        minWidth: 70,
        width: '*',
        align: 'left',
        isReadOnly: true,
      },
      ...hepTypes.map(countColumn),
      {
        binding: 'spTpCd',
        visible: false,
      },
      {
        binding: 'prjSpCd',
        visible: false,
      },
      {
        binding: 'prjSpDivsCd',
        visible: false,
      },
      {
        binding: 'elmPrjId',
        visible: false,
      },
      {
        binding: 'excpId',
        visible: false,
      },
    ],
    [isReadOnly, hepTypes]
  );

  const isCountBinding = (binding: string) => hepTypes.some((type) => type.cmnCd === binding);

  const onFormatItem = useEvent((grid, e) => {
    if (e.panel.cellType !== CellType.Cell) return;
    const binding = e.panel.columns[e.col]?.binding;
    const item = e.panel.rows[e.row]?.dataItem;
    const isEditing = !!grid.editRange?.contains(e.row, e.col);
    const hasValue = !isEditing && isCountBinding(binding) && Number(item?.[binding] || 0) !== 0;
    e.cell.style.backgroundColor = hasValue ? InfoBG.ColorB : ''; // [26.09.30] 값 있는 셀 색상 표시
    e.cell.style.color = hasValue ? InfoText.ColorB : '';
    e.cell.style.fontWeight = hasValue ? 'bold' : '';
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.formatItem.addHandler(onFormatItem);
  };

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    if (isCountBinding(binding)) {
      const newVal = Number(grid.activeEditor?.value);
      if (Number.isNaN(newVal) || newVal < 0) {
        e.cancel = true;
      }
    }
  });

  const onCellEditEnded = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    if (isCountBinding(binding)) {
      const data = grid.rows[e.row].dataItem;
      const newVal = data[binding];
      data[binding] = newVal ? Math.trunc(Number(newVal)) : '';
    }
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleSave}
      disableRipple
    >
      {t('com.button.저장', '저장')}
    </Button>,
  ];

  return (
    <CustomDialog
      title={t('setup.label.중장비 등록', '중장비 등록')}
      size={'md'}
      open={open}
      onClose={close}
      onCancel={isReadOnly ? undefined : close}
      buttons={isReadOnly ? undefined : dialogButtons}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>
            {condition?.eqpId} : {condition?.eqpNm}
          </h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={613}
        isSelector={false}
        isHoverCss={false}
        allowDragging={false}
        initialized={onInitialized}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
      />
    </CustomDialog>
  );
};

export default SetupDailyReportHeavyEquipmentPopup;
